import React, { useState, useEffect, useCallback } from 'react';
import { adminFetch } from '../../utils/auth';
import { getBackendUrl } from '../../utils/api';
import { exportToCSV } from '../../utils/export';
import { getRouteName } from '../../utils/routeNames';
import { DateRangePicker } from '../../components/admin/DateRangePicker';
import HierarchyFilter, { HierarchyFilterValues } from '../../components/admin/HierarchyFilter';
import { formatISTSplit } from '../../utils/dateFormat';
import ImagePreview from '../../components/admin/ImagePreview';

// Ant Design imports
import {
  Card,
  Typography,
  Button,
  Alert,
  Space,
  Input,
  Tag,
  Select,
  Switch,
  Tooltip,
  Table,
  Flex,
  Empty,
  Drawer,
  Spin,
  Divider,
  Modal,
} from 'antd';
import type { TableColumnsType } from 'antd';

// Ant Design Icons
import {
  ReloadOutlined,
  DownloadOutlined,
  ClearOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  MinusCircleFilled,
  ThunderboltFilled,
  ClockCircleOutlined,
  SoundOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
} from '@ant-design/icons';

const { Title, Text } = Typography;

// Audio Player Component for voice search recordings
interface AudioPlayerProps {
  audioId: string;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ audioId }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [error, setError] = useState(false);
  const audioRef = React.useRef<HTMLAudioElement>(null);

  const audioUrl = `${getBackendUrl()}/api/asr/audio/${audioId}`;

  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(() => setError(true));
    }
    setIsPlaying(!isPlaying);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  if (error) {
    return (
      <div style={{
        background: '#fff2f0',
        border: '1px solid #ffccc7',
        borderRadius: 8,
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}>
        <SoundOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
        <Text type="secondary" style={{ fontSize: 13 }}>Audio file not available</Text>
      </div>
    );
  }

  return (
    <div style={{
      background: '#f0f5ff',
      border: '1px solid #adc6ff',
      borderRadius: 8,
      padding: '12px 16px',
    }}>
      <audio
        ref={audioRef}
        src={audioUrl}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        onError={() => setError(true)}
        preload="metadata"
      />
      <Flex align="center" gap={12}>
        <Button
          type="primary"
          shape="circle"
          icon={isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
          onClick={togglePlay}
          style={{
            background: '#1890ff',
            borderColor: '#1890ff',
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        />
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <SoundOutlined style={{ color: '#1890ff', fontSize: 14 }} />
            <Text style={{ fontSize: 13, fontWeight: 500 }}>Voice Recording</Text>
          </div>
          <Flex align="center" gap={8}>
            <Text type="secondary" style={{ fontSize: 11, minWidth: 35 }}>
              {formatTime(currentTime)}
            </Text>
            <input
              type="range"
              min={0}
              max={duration || 100}
              value={currentTime}
              onChange={handleSeek}
              style={{
                flex: 1,
                height: 4,
                cursor: 'pointer',
                accentColor: '#1890ff',
              }}
            />
            <Text type="secondary" style={{ fontSize: 11, minWidth: 35 }}>
              {formatTime(duration)}
            </Text>
          </Flex>
        </div>
      </Flex>
    </div>
  );
};

// Helper function to format time - shows seconds when >= 1000ms
const formatDuration = (ms: number | undefined | null): string => {
  if (typeof ms !== 'number' || isNaN(ms)) return 'N/A';
  if (ms >= 1000) {
    return `${(ms / 1000).toFixed(1)}s`;
  }
  return `${ms.toFixed(0)}ms`;
};

// ─── Last Updated Indicator ─────────────────────────────────────────
const LastUpdated: React.FC<{ timestamp: Date | null }> = ({ timestamp }) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setTick((t) => t + 1), 10000);
    return () => clearInterval(iv);
  }, []);
  if (!timestamp) return null;
  const secs = Math.floor((Date.now() - timestamp.getTime()) / 1000);
  const label = secs < 10 ? 'just now' : secs < 60 ? `${secs}s ago` : `${Math.floor(secs / 60)}m ago`;
  return (
    <Flex align="center" gap={6}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#52c41a', display: 'inline-block', animation: 'logsPulse 2s infinite' }} />
      <Text type="secondary" style={{ fontSize: 12 }}>Updated {label}</Text>
      <style>{`@keyframes logsPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
    </Flex>
  );
};

// ─── Empty State Helper ─────────────────────────────────────────────
const EmptyState = ({ description = 'No data available — try adjusting your date range or filters' }: { description?: string }) => (
  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<Text type="secondary">{description}</Text>} />
);

// ─── Row Highlighting Helper ────────────────────────────────────────
const getRowStyle = (record: any) => {
  const code = record.status_code || record.statusCode;
  if (code >= 500) return { style: { backgroundColor: 'rgba(255,77,79,0.04)' } };
  if (code >= 400) return { style: { backgroundColor: 'rgba(250,173,20,0.04)' } };
  return {};
};

// ─── Expandable Row Config ──────────────────────────────────────────
const expandableConfig = {
  expandedRowRender: (record: any) => (
    <pre style={{ margin: 0, fontSize: 12, maxHeight: 300, overflow: 'auto', background: '#fafafa', padding: 12, borderRadius: 6 }}>
      {JSON.stringify(
        { payload: record.payload || record.request_body, response: record.response || record.response_body, metadata: record.metadata },
        null,
        2,
      )}
    </pre>
  ),
  rowExpandable: (record: any) => !!(record.payload || record.request_body || record.response || record.response_body || record.metadata),
};

// Step category colors and non-technical labels
const categoryConfig: Record<string, { color: string; label: string }> = {
  ai_processing: { color: '#1890ff', label: 'Smart Processing' },
  search: { color: '#52c41a', label: 'Search' },
  external_api: { color: '#fa8c16', label: 'Catalog Lookup' },
  caching: { color: '#8c8c8c', label: 'Cache' },
  detection: { color: '#722ed1', label: 'Detection' },
  input_processing: { color: '#13c2c2', label: 'Input Cleanup' },
  validation: { color: '#f5222d', label: 'Validation' },
  file_io: { color: '#2f54eb', label: 'File Handling' },
  processing: { color: '#eb2f96', label: 'Processing' },
  user_interaction: { color: '#597ef7', label: 'User Input' },
};

// Step name non-technical labels + descriptions
const stepNameLabels: Record<string, { label: string; description: string }> = {
  query_sanitization: { label: 'Clean Search Text', description: 'Removing special characters and formatting the search query' },
  cache_lookup: { label: 'Check Previous Results', description: 'Looking up if this exact search was done recently' },
  nlp_processing: { label: 'Understand Search Intent', description: 'Analyzing the search query to extract vehicle make, model, part details, and other information' },
  part_number_detection: { label: 'Detect Part Number', description: 'Checking if the query contains a specific part number' },
  vehicle_number_detection: { label: 'Detect Vehicle Number', description: 'Checking if the query contains a vehicle registration number' },
  oem_part_search: { label: 'Search by Part Number', description: 'Looking up results directly using the detected part number' },
  part_details_extraction: { label: 'Extract Part Details', description: 'Identifying specific part attributes from the search query' },
  query_enhancement: { label: 'Improve Search Query', description: 'Expanding and refining the query for better results' },
  vector_search: { label: 'Similarity Search', description: 'Finding similar parts using smart matching' },
  final_part_extraction: { label: 'Finalize Part Info', description: 'Confirming the final part details before catalog search' },
  vehicle_name_normalization: { label: 'Standardize Vehicle Name', description: 'Matching vehicle make and model to standard catalog names' },
  external_api_search: { label: 'Search Parts Catalog', description: 'Fetching results from external parts catalogs' },
  external_api_search_by_source: { label: 'Search Parts Catalog', description: 'Fetching results from the selected catalog source' },
  fallback_public_search: { label: 'Broader Search', description: 'Running a wider search when specific catalog search found no results' },
  results_caching: { label: 'Save Results for Reuse', description: 'Storing results so the same search is faster next time' },
  mandatory_fields_validation: { label: 'Check Required Fields', description: 'Verifying all necessary vehicle and part details are provided' },
  missing_fields_detection: { label: 'Identify Missing Info', description: 'Determining which required fields are still missing' },
  intent_detection: { label: 'Detect Search Intent', description: 'Understanding whether this is a part search, vehicle lookup, or general inquiry' },
  user_provided_overrides: { label: 'User Provided Details', description: 'Additional information filled in by the user after initial search' },
  vehicle_details_extraction: { label: 'Extract Vehicle Info', description: 'Pulling vehicle make, model, variant, and fuel type from the query' },
  part_name_extraction: { label: 'Extract Part Name', description: 'Identifying the specific part being searched for' },
  part_description_cleaning: { label: 'Clean Part Description', description: 'Standardizing the part name for catalog matching' },
  audio_file_save: { label: 'Save Audio Recording', description: 'Storing the uploaded voice recording for processing' },
  audio_transcription: { label: 'Convert Voice to Text', description: 'Transcribing the voice recording into searchable text' },
  image_semantic_validation: { label: 'Validate Image Content', description: 'Checking if the uploaded image contains an auto part' },
  image_file_save: { label: 'Save Uploaded Image', description: 'Storing the uploaded image for analysis' },
  gemini_vision_analysis: { label: 'Analyze Image', description: 'Using AI to identify the part in the uploaded image' },
  image_processing: { label: 'Process Image', description: 'Preparing the image for analysis' },
  hybrid_search_processing: { label: 'Combined Search', description: 'Running both image-based and text-based search together' },
  results_aggregation: { label: 'Combine Results', description: 'Merging and ranking results from all sources' },
};

const getStepLabel = (stepName: string): string => {
  return stepNameLabels[stepName]?.label || stepName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const getStepDescription = (stepName: string): string => {
  return stepNameLabels[stepName]?.description || '';
};

// System logs expandable config
const systemExpandableConfig = {
  expandedRowRender: (record: any) => (
    <pre style={{ margin: 0, fontSize: 12, maxHeight: 300, overflow: 'auto', background: '#fafafa', padding: 12, borderRadius: 6 }}>
      {JSON.stringify(
        { message: record.message, details: record.details, stack_trace: record.stack_trace, metadata: record.metadata },
        null,
        2,
      )}
    </pre>
  ),
  rowExpandable: (record: any) => !!(record.details || record.stack_trace || record.metadata),
};

type TabType = 'event-logs' | 'search-history' | 'system-logs';

interface LogsProps {
  initialTab?: TabType;
}

function Logs({ initialTab = 'event-logs' }: LogsProps) {
  const [logs, setLogs] = useState<any[]>([]);
  const [searchHistory, setSearchHistory] = useState<any[]>([]);
  const [systemLogs, setSystemLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchHistoryLoading, setSearchHistoryLoading] = useState(true);
  const [systemLogsLoading, setSystemLogsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchHistoryError, setSearchHistoryError] = useState<string>('');
  const [systemLogsError, setSystemLogsError] = useState<string>('');
  const [logType, setLogType] = useState<string>('');
  const [route, setRoute] = useState<string>('');
  const [searchTypeFilter, setSearchTypeFilter] = useState<string>('');
  const [sessionIdFilter, setSessionIdFilter] = useState<string>('');
  const [systemLogLevelFilter, setSystemLogLevelFilter] = useState<string>('');
  const [systemLogServiceFilter, setSystemLogServiceFilter] = useState<string>('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [logsTotal, setLogsTotal] = useState(0);
  const [searchHistoryTotal, setSearchHistoryTotal] = useState(0);
  const [systemLogsTotal, setSystemLogsTotal] = useState(0);
  // Pagination state for all three tables
  const [logsPage, setLogsPage] = useState(1);
  const [logsPageSize, setLogsPageSize] = useState(25);
  const [searchPage, setSearchPage] = useState(1);
  const [searchPageSize, setSearchPageSize] = useState(25);
  const [sysLogsPage, setSysLogsPage] = useState(1);
  const [sysLogsPageSize, setSysLogsPageSize] = useState(25);

  const [travelLogCache, setTravelLogCache] = useState<Record<number, any>>({});
  const [travelLogLoading, setTravelLogLoading] = useState<Record<number, boolean>>({});
  const [apiCallsCache, setApiCallsCache] = useState<Record<number, any>>({});

  const [hierarchyFilter, setHierarchyFilter] = useState<HierarchyFilterValues>({
    tenantId: null,
    businessId: null,
    applicationId: null,
  });
  const selectedTenantId = hierarchyFilter.tenantId;
  const selectedBusinessId = hierarchyFilter.businessId;
  const selectedApplicationId = hierarchyFilter.applicationId;

  // Error drill-down: read sessionStorage on mount for event-logs tab
  useEffect(() => {
    if (initialTab === 'event-logs') {
      try {
        const drillDown = sessionStorage.getItem('errorDrillDown');
        if (drillDown) {
          const { statusCode, route: drillRoute } = JSON.parse(drillDown);
          if (statusCode) setLogType(String(statusCode));
          if (drillRoute) setRoute(drillRoute);
          sessionStorage.removeItem('errorDrillDown');
        }
      } catch {
        // ignore
      }
    }
  }, [initialTab]);

  const handleDateRangeChange = (start: Date | null, end: Date | null) => {
    setStartDate(start);
    setEndDate(end);
  };

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      if (logType) params.append('log_type', logType);
      if (route) params.append('route', route);
      if (startDate) params.append('start_date', startDate.toISOString());
      if (endDate) params.append('end_date', endDate.toISOString());
      if (selectedTenantId) params.append('tenant_id', selectedTenantId);
      if (selectedBusinessId) params.append('business_id', selectedBusinessId);
      if (selectedApplicationId) params.append('application_id', selectedApplicationId);
      params.append('limit', '1000');

      const response = await adminFetch(`${getBackendUrl()}/api/analytics/logs?${params}`);
      if (response.ok) {
        const data = await response.json();
        setLogs(data.data || []);
        setLogsTotal(data.total || (data.data || []).length);
        setError('');
        setLastUpdated(new Date());
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        setError(`Failed to load logs: ${errorData.message || 'Unknown error'}`);
      }
    } catch (err: any) {
      setError(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [logType, route, startDate, endDate, selectedTenantId, selectedBusinessId, selectedApplicationId]);

  const loadSearchHistory = useCallback(async () => {
    try {
      setSearchHistoryLoading(true);

      const params = new URLSearchParams();
      if (searchTypeFilter) params.append('search_type', searchTypeFilter);
      if (sessionIdFilter) params.append('session_id', sessionIdFilter);
      if (startDate) params.append('start_date', startDate.toISOString());
      if (endDate) params.append('end_date', endDate.toISOString());
      if (selectedTenantId) params.append('tenant_id', selectedTenantId);
      if (selectedBusinessId) params.append('business_id', selectedBusinessId);
      if (selectedApplicationId) params.append('application_id', selectedApplicationId);
      params.append('limit', '1000');

      // Search history logs are served by the search-service under /api/search/admin/logs
      // This avoids routing conflicts with /api/admin which goes to client-management
      const response = await adminFetch(`${getBackendUrl()}/api/search/admin/logs?${params}`);
      if (response.ok) {
        const data = await response.json();
        setSearchHistory(data.data || []);
        setSearchHistoryTotal(data.total || (data.data || []).length);
        setSearchHistoryError('');
        setLastUpdated(new Date());
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        setSearchHistoryError(`Failed to load search history: ${errorData.message || 'Unknown error'}`);
      }
    } catch (err: any) {
      setSearchHistoryError(`Error: ${err.message}`);
    } finally {
      setSearchHistoryLoading(false);
    }
  }, [searchTypeFilter, sessionIdFilter, startDate, endDate, selectedTenantId, selectedBusinessId, selectedApplicationId]);

  const fetchTravelLog = async (searchHistoryId: number) => {
    if (travelLogCache[searchHistoryId]) return; // Already cached
    setTravelLogLoading(prev => ({ ...prev, [searchHistoryId]: true }));
    try {
      // Fetch travel log and API call counts in parallel
      const [travelRes, apiCallsRes] = await Promise.all([
        adminFetch(`${getBackendUrl()}/api/search/admin/travel-log/${searchHistoryId}`),
        adminFetch(`${getBackendUrl()}/api/search/admin/search/${searchHistoryId}/api-calls`),
      ]);

      if (travelRes.ok) {
        const result = await travelRes.json();
        if (result.success && result.data) {
          setTravelLogCache(prev => ({ ...prev, [searchHistoryId]: result.data }));
        }
      }

      if (apiCallsRes.ok) {
        const apiResult = await apiCallsRes.json();
        if (apiResult.success && apiResult.data) {
          setApiCallsCache(prev => ({ ...prev, [searchHistoryId]: apiResult.data }));
        }
      }
    } catch (err) {
      console.error('Failed to fetch travel log:', err);
    } finally {
      setTravelLogLoading(prev => ({ ...prev, [searchHistoryId]: false }));
    }
  };

  const loadSystemLogs = useCallback(async () => {
    try {
      setSystemLogsLoading(true);

      const params = new URLSearchParams();
      if (systemLogLevelFilter) params.append('level', systemLogLevelFilter);
      if (systemLogServiceFilter) params.append('service', systemLogServiceFilter);
      if (startDate) params.append('start_date', startDate.toISOString());
      if (endDate) params.append('end_date', endDate.toISOString());
      params.append('limit', '1000');

      const response = await adminFetch(`${getBackendUrl()}/api/analytics/system-logs?${params}`);
      if (response.ok) {
        const data = await response.json();
        setSystemLogs(data.data || []);
        setSystemLogsTotal(data.total || (data.data || []).length);
        setSystemLogsError('');
        setLastUpdated(new Date());
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        setSystemLogsError(`Failed to load system logs: ${errorData.message || 'Unknown error'}`);
      }
    } catch (err: any) {
      setSystemLogsError(`Error: ${err.message}`);
    } finally {
      setSystemLogsLoading(false);
    }
  }, [systemLogLevelFilter, systemLogServiceFilter, startDate, endDate]);

  const handleClearSystemLogs = () => {
    Modal.confirm({
      title: 'Clear All System Logs',
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete all ${systemLogsTotal} system log entries? This action cannot be undone.`,
      okText: 'Clear All',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const response = await adminFetch(`${getBackendUrl()}/api/analytics/system-logs`, { method: 'DELETE' });
          if (response.ok) {
            setSystemLogs([]);
            setSystemLogsTotal(0);
            setSysLogsPage(1);
            setLastUpdated(new Date());
            setSystemLogsError('');
          } else {
            const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
            setSystemLogsError(`Failed to clear logs: ${errorData.message || 'Unknown error'}`);
          }
        } catch (err: any) {
          setSystemLogsError(`Error clearing logs: ${err.message}`);
        }
      },
    });
  };

  useEffect(() => {
    if (initialTab === 'event-logs') {
      loadLogs();
    } else if (initialTab === 'search-history') {
      loadSearchHistory();
    } else if (initialTab === 'system-logs') {
      loadSystemLogs();
    }

    let interval: NodeJS.Timeout | null = null;
    if (autoRefresh) {
      interval = setInterval(() => {
        if (initialTab === 'event-logs') {
          loadLogs();
        } else if (initialTab === 'search-history') {
          loadSearchHistory();
        } else if (initialTab === 'system-logs') {
          loadSystemLogs();
        }
      }, 10000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [initialTab, autoRefresh, loadLogs, loadSearchHistory, loadSystemLogs]);

  // ─── Drawer state for search travel log ───────────────────────────
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewImageIds, setPreviewImageIds] = useState<string[]>([]);
  const [drawerRecord, setDrawerRecord] = useState<any>(null);
  const [expandedSteps, setExpandedSteps] = useState<Record<number, boolean>>({});

  const openTravelDrawer = (record: any) => {
    const id = record.id;
    setDrawerRecord(record);
    setDrawerOpen(true);
    setExpandedSteps({});
    fetchTravelLog(id);
  };

  const closeTravelDrawer = () => {
    setDrawerOpen(false);
    setDrawerRecord(null);
    setExpandedSteps({});
  };

  const toggleStep = (stepOrder: number) => {
    setExpandedSteps(prev => ({ ...prev, [stepOrder]: !prev[stepOrder] }));
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success': return <CheckCircleFilled style={{ color: '#52c41a', fontSize: 18 }} />;
      case 'error': return <CloseCircleFilled style={{ color: '#f5222d', fontSize: 18 }} />;
      case 'skipped': return <MinusCircleFilled style={{ color: '#8c8c8c', fontSize: 18 }} />;
      case 'cached': return <ThunderboltFilled style={{ color: '#faad14', fontSize: 18 }} />;
      default: return <CheckCircleFilled style={{ color: '#52c41a', fontSize: 18 }} />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'success': return 'Completed';
      case 'error': return 'Failed';
      case 'skipped': return 'Skipped';
      case 'cached': return 'From Cache';
      default: return status;
    }
  };

  // Friendly field name mapping for clean display
  const fieldLabels: Record<string, string> = {
    query: 'Search Query', original_query: 'Original Query', structured_query: 'Extracted Details',
    make: 'Make', model: 'Model', variant: 'Variant', fuelType: 'Fuel Type', year: 'Year',
    brand: 'Brand', aggregate: 'Aggregate', subAggregate: 'Part Category', partNumber: 'Part Number',
    partDescription: 'Part Description', source: 'Source', limit: 'Max Results',
    missing_mmvfy: 'Missing Vehicle Info', missing_subaggregate: 'Missing Part Info', passed: 'Passed',
    detected_vehicle_number: 'Vehicle Number', detected_part_number: 'Part Number',
    exclude_vehicle_number: 'Excluded Vehicle Number',
    results_count: 'Results Found', from_cache: 'From Cache', note: 'Note',
    overrides: 'User Filled Details',
    initial_query: 'Initial Search', updated_query: 'Updated Search',
    user_provided: 'What User Added', additional_terms: 'Additional Terms', info: 'Note',
    intent: 'Intent', type: 'Type', confidence: 'Confidence',
    part_name: 'Part Name', extracted_sub_aggregate: 'Extracted Part Category', extraction_source: 'Source',
    query_context: 'Context', vehicle_details: 'Vehicle Details',
    error: 'Error', message: 'Message',
  };

  // Internal fields to hide from user-facing display
  const hiddenFields = new Set(['correlation_id', 'original_query', 'exclude_vehicle_number']);

  const getFieldLabel = (key: string) => fieldLabels[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  // Render a data object as clean key-value rows
  const renderDataSummary = (data: any, depth = 0): React.ReactNode => {
    if (data === null || data === undefined) return <span style={{ color: '#8c8c8c' }}>—</span>;
    if (typeof data === 'boolean') return <span style={{ color: data ? '#52c41a' : '#f5222d' }}>{data ? 'Yes' : 'No'}</span>;
    if (typeof data === 'number') return <span>{data}</span>;
    if (typeof data === 'string') return <span>{data || '—'}</span>;
    if (Array.isArray(data)) {
      if (data.length === 0) return <span style={{ color: '#8c8c8c' }}>None</span>;
      return <span>{data.join(', ')}</span>;
    }
    if (typeof data === 'object') {
      const entries = Object.entries(data).filter(([k, v]) => v !== null && v !== undefined && v !== '' && !hiddenFields.has(k));
      if (entries.length === 0) return <span style={{ color: '#8c8c8c' }}>—</span>;
      return (
        <div style={{ paddingLeft: depth > 0 ? 12 : 0, borderLeft: depth > 0 ? '2px solid #f0f0f0' : 'none' }}>
          {entries.map(([key, val]) => (
            <div key={key} style={{ display: 'flex', gap: 8, padding: '3px 0', fontSize: 12, lineHeight: 1.6 }}>
              <span style={{ color: '#8c8c8c', minWidth: depth > 0 ? 100 : 120, flexShrink: 0, fontWeight: 500 }}>
                {getFieldLabel(key)}
              </span>
              <span style={{ color: '#262626', wordBreak: 'break-word', flex: 1 }}>
                {typeof val === 'object' && val !== null && !Array.isArray(val)
                  ? renderDataSummary(val, depth + 1)
                  : renderDataSummary(val, depth)
                }
              </span>
            </div>
          ))}
        </div>
      );
    }
    return <span>{String(data)}</span>;
  };

  const renderTravelDrawer = () => {
    if (!drawerRecord) return null;
    const id = drawerRecord.id;
    const data = travelLogCache[id];
    const isLoading = travelLogLoading[id];

    return (
      <Drawer
        title={
          <div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>Search Details</div>
            <div style={{ fontSize: 13, color: '#8c8c8c', fontWeight: 400, marginTop: 2 }}>
              {drawerRecord.query_text || drawerRecord.image_path || 'N/A'}
            </div>
          </div>
        }
        placement="right"
        width={640}
        open={drawerOpen}
        onClose={closeTravelDrawer}
        styles={{ body: { padding: 0, background: '#f5f5f5' } }}
      >
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
            <Spin size="large" tip="Loading search details..." />
          </div>
        ) : !data ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <Empty description="No detailed log available. Pipeline tracking is enabled for new searches only." />
          </div>
        ) : (
          <div>
            {/* ── Summary Cards ─────────────────────────────── */}
            <div style={{ padding: '20px 24px 0' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12 }}>
                <div style={{ background: '#fff', padding: '12px 16px', borderRadius: 8, border: '1px solid #f0f0f0' }}>
                  <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 4 }}>Tenant</div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{data.customer?.tenant_name || data.customer?.tenant_id || 'N/A'}</div>
                  {data.customer?.primary_contact && <div style={{ fontSize: 11, color: '#8c8c8c' }}>{data.customer.primary_contact}</div>}
                </div>
                <div style={{ background: '#fff', padding: '12px 16px', borderRadius: 8, border: '1px solid #f0f0f0' }}>
                  <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 4 }}>Business</div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{data.customer?.business_id || 'N/A'}</div>
                </div>
                <div style={{ background: '#fff', padding: '12px 16px', borderRadius: 8, border: '1px solid #f0f0f0' }}>
                  <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 4 }}>Application</div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{data.customer?.application_id || 'N/A'}</div>
                </div>
                <div style={{ background: '#fff', padding: '12px 16px', borderRadius: 8, border: '1px solid #f0f0f0' }}>
                  <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 4 }}>Total Time</div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{formatDuration(data.response_time_ms)}</div>
                </div>
                <div style={{ background: '#fff', padding: '12px 16px', borderRadius: 8, border: '1px solid #f0f0f0' }}>
                  <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 4 }}>Results Found</div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{data.results_count ?? 0}</div>
                </div>
                <div style={{ background: '#fff', padding: '12px 16px', borderRadius: 8, border: '1px solid #f0f0f0' }}>
                  <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 4 }}>Type</div>
                  <div style={{ fontSize: 14, fontWeight: 600, textTransform: 'capitalize' }}>{data.search_type || drawerRecord?.search_type || 'N/A'}</div>
                </div>
                <div style={{ background: '#fff', padding: '12px 16px', borderRadius: 8, border: '1px solid #f0f0f0' }}>
                  <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 4 }}>Session ID</div>
                  <Tooltip title={drawerRecord?.session_id || ''}>
                    <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{drawerRecord?.session_id || 'N/A'}</div>
                  </Tooltip>
                </div>
                {(data.correlation_id || drawerRecord?.correlation_id) && (
                  <div style={{ background: '#fff', padding: '12px 16px', borderRadius: 8, border: '1px solid #f0f0f0' }}>
                    <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 4 }}>Correlation ID</div>
                    <Tooltip title={data.correlation_id || drawerRecord?.correlation_id}>
                      <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(data.correlation_id || drawerRecord?.correlation_id)?.substring(0, 12)}...</div>
                    </Tooltip>
                  </div>
                )}
                {data.vehicle_number && (
                  <div style={{ background: '#fff', padding: '12px 16px', borderRadius: 8, border: '1px solid #f0f0f0' }}>
                    <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 4 }}>Vehicle Number</div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{data.vehicle_number}</div>
                  </div>
                )}
                {data.part_number_detected && (
                  <div style={{ background: '#fff', padding: '12px 16px', borderRadius: 8, border: '1px solid #f0f0f0' }}>
                    <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 4 }}>Part Number</div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{data.part_number_detected}</div>
                  </div>
                )}
              </div>
            </div>

            {/* ── Uploaded Image Preview (for IMAGE/HYBRID searches) ────────────────────── */}
            {drawerRecord?.image_path && drawerRecord?.search_type !== 'ASR' && (
              <div style={{ padding: '16px 24px 0' }}>
                <Divider orientation="left" style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 600 }}>
                  Uploaded Image{drawerRecord.image_path.split(',').length > 1 ? 's' : ''}
                </Divider>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {drawerRecord.image_path.split(',').map((id: string) => (
                    <ImagePreview
                      key={id.trim()}
                      imageId={id.trim()}
                      size={120}
                      onClick={() => { setPreviewImageIds(drawerRecord.image_path.split(',').map((i: string) => i.trim())); setPreviewModalOpen(true); }}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* ── Audio Recording (for ASR/Voice searches) ────────────────────── */}
            {drawerRecord?.search_type === 'ASR' && drawerRecord?.image_path && (
              <div style={{ padding: '16px 24px 0' }}>
                <Divider orientation="left" style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 600 }}>
                  Voice Recording
                </Divider>
                <AudioPlayer audioId={drawerRecord.image_path.trim()} />
              </div>
            )}

            {/* ── Search Chain ──────────────────────────────── */}
            {data.chain && data.chain.length > 1 && (
              <div style={{ padding: '16px 24px 0' }}>
                <div style={{ background: '#e6f7ff', borderRadius: 8, padding: '14px 18px', border: '1px solid #91d5ff' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#1890ff', marginBottom: 10 }}>Search Chain</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {data.chain.map((entry: any, idx: number) => {
                      const isCurrent = entry.search_history_id === data.search_history_id;
                      const typeLabel = entry.search_type === 'ASR' ? 'Voice' : entry.search_type === 'IMAGE' ? 'Image' : entry.search_type === 'HYBRID' ? 'Image + Text' : 'Text';
                      return (
                        <React.Fragment key={entry.search_history_id}>
                          <div style={{
                            padding: '6px 12px', borderRadius: 6,
                            background: isCurrent ? '#1890ff' : '#fff',
                            color: isCurrent ? '#fff' : '#333',
                            border: `1px solid ${isCurrent ? '#1890ff' : '#d9d9d9'}`,
                            fontSize: 12, lineHeight: 1.4,
                          }}>
                            <div style={{ fontWeight: 600 }}>{typeLabel}</div>
                            {entry.query_text && (
                              <div style={{ opacity: 0.8, fontSize: 11, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {entry.query_text}
                              </div>
                            )}
                            <div style={{ fontSize: 10, opacity: 0.7, marginTop: 2 }}>
                              {formatDuration(entry.response_time_ms)} {entry.status === 'success' ? ' - Done' : entry.status === 'error' ? ' - Failed' : ''}
                            </div>
                          </div>
                          {idx < data.chain.length - 1 && (
                            <span style={{ color: '#1890ff', fontSize: 18, fontWeight: 'bold', lineHeight: 1 }}>{'\u2192'}</span>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* ── API Call Counts ───────────────────────────── */}
            {(() => {
              const apiData = apiCallsCache[id];
              if (!apiData || !apiData.breakdown || apiData.breakdown.length === 0) return null;
              const billable = apiData.breakdown.filter((item: any) => item.billable !== false);
              const nonBillable = apiData.breakdown.filter((item: any) => item.billable === false);
              return (
                <div style={{ padding: '16px 24px 0' }}>
                  <Divider orientation="left" style={{ margin: '0 0 16px', fontSize: 13, fontWeight: 600 }}>
                    API Calls ({apiData.total_external_calls || 0} total)
                  </Divider>
                  {billable.length > 0 && (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                        <Text style={{ fontSize: 12, fontWeight: 600 }}>External Billable</Text>
                        <Tag color="red" style={{ margin: 0, fontSize: 10 }}>Paid</Tag>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, marginBottom: 12 }}>
                        {billable.map((item: any) => (
                          <div key={item.type} style={{
                            background: '#fff7f7', padding: '10px 14px', borderRadius: 8,
                            border: '1px solid #ffccc7', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          }}>
                            <span style={{ fontSize: 12, color: '#595959' }}>{item.label}</span>
                            <Tag color="red" style={{ margin: 0, fontWeight: 600 }}>{item.count}</Tag>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  {nonBillable.length > 0 && (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                        <Text style={{ fontSize: 12, fontWeight: 600 }}>Non-Billable</Text>
                        <Tag color="green" style={{ margin: 0, fontSize: 10 }}>Internal</Tag>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
                        {nonBillable.map((item: any) => (
                          <div key={item.type} style={{
                            background: '#f6ffed', padding: '10px 14px', borderRadius: 8,
                            border: '1px solid #b7eb8f', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          }}>
                            <span style={{ fontSize: 12, color: '#595959' }}>{item.label}</span>
                            <Tag color="green" style={{ margin: 0, fontWeight: 600 }}>{item.count}</Tag>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              );
            })()}

            {/* ── Pipeline Steps (Vertical Stepper) ─────────── */}
            <div style={{ padding: '20px 24px 24px' }}>
              <Divider orientation="left" style={{ margin: '0 0 20px', fontSize: 13, fontWeight: 600 }}>
                Processing Steps ({data.steps?.length || 0})
              </Divider>

              {data.steps && data.steps.length > 0 ? (
                <div style={{ position: 'relative', paddingLeft: 40 }}>
                  {data.steps.map((step: any, idx: number) => {
                    const cat = categoryConfig[step.step_category] || { color: '#8c8c8c', label: step.step_category };
                    const isExpanded = expandedSteps[step.step_order];
                    const isLast = idx === data.steps.length - 1;
                    const isFirst = idx === 0;

                    return (
                      <div key={step.step_order} style={{ position: 'relative' }}>
                        {/* Connector line to next step */}
                        {!isLast && (
                          <div style={{
                            position: 'absolute', left: -26, top: '50%',
                            bottom: -16, width: 2, background: '#e8e8e8',
                          }} />
                        )}
                        {/* Connector line from previous step */}
                        {!isFirst && (
                          <div style={{
                            position: 'absolute', left: -26, top: -16,
                            bottom: '50%', width: 2, background: '#e8e8e8',
                          }} />
                        )}

                        {/* Timeline dot */}
                        <div style={{
                          position: 'absolute', left: -40, top: '50%', transform: 'translateY(-50%)',
                          width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: '#fff', borderRadius: '50%', zIndex: 1,
                          border: '2px solid #f0f0f0',
                        }}>
                          {getStatusIcon(step.status)}
                        </div>

                        {/* Step card */}
                        <div style={{
                          background: '#fff', borderRadius: 8, padding: '14px 18px',
                          border: step.status === 'error' ? '1px solid #ffa39e' : '1px solid #f0f0f0',
                          marginBottom: isLast ? 0 : 16,
                        }}>
                          {/* Step header row */}
                          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, fontSize: 13, color: '#262626', marginBottom: 2 }}>
                                {getStepLabel(step.step_name)}
                              </div>
                              {getStepDescription(step.step_name) && (
                                <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 6 }}>
                                  {getStepDescription(step.step_name)}
                                </div>
                              )}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                              <Tag color={cat.color} style={{ margin: 0, fontSize: 11 }}>{cat.label}</Tag>
                              <Tooltip title={`Status: ${getStatusLabel(step.status)}`}>
                                <Tag
                                  color={step.status === 'success' ? 'green' : step.status === 'error' ? 'red' : step.status === 'cached' ? 'gold' : 'default'}
                                  style={{ margin: 0, fontSize: 11 }}
                                >
                                  {getStatusLabel(step.status)}
                                </Tag>
                              </Tooltip>
                            </div>
                          </div>

                          {/* Duration + error */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
                            <span style={{ fontSize: 12, color: '#8c8c8c', display: 'flex', alignItems: 'center', gap: 4 }}>
                              <ClockCircleOutlined style={{ fontSize: 11 }} />
                              {formatDuration(step.duration_ms)}
                            </span>
                            {step.error_message && (
                              <span style={{ fontSize: 12, color: '#f5222d' }}>
                                {step.error_message.length > 120 ? step.error_message.substring(0, 120) + '...' : step.error_message}
                              </span>
                            )}
                          </div>

                          {/* Expandable data */}
                          {(step.input_data || step.output_data) && (
                            <div style={{ marginTop: 8 }}>
                              <Button
                                type="link"
                                size="small"
                                onClick={() => toggleStep(step.step_order)}
                                style={{ padding: 0, fontSize: 12, height: 'auto' }}
                              >
                                {isExpanded ? 'Hide Details' : 'View Details'}
                              </Button>
                              {isExpanded && (
                                <div style={{ marginTop: 8 }}>
                                  {step.input_data && (
                                    <div style={{ marginBottom: 10 }}>
                                      <div style={{ fontSize: 11, fontWeight: 600, color: '#1890ff', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Input</div>
                                      <div style={{ background: '#fafafa', padding: 12, borderRadius: 6, border: '1px solid #f0f0f0' }}>
                                        {renderDataSummary(step.input_data)}
                                      </div>
                                    </div>
                                  )}
                                  {step.output_data && (
                                    <div>
                                      <div style={{ fontSize: 11, fontWeight: 600, color: '#52c41a', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Result</div>
                                      <div style={{ background: '#fafafa', padding: 12, borderRadius: 6, border: '1px solid #f0f0f0' }}>
                                        {renderDataSummary(step.output_data)}
                                      </div>
                                    </div>
                                  )}
                                  {/* Audio Player for voice transcription step */}
                                  {step.step_name === 'audio_transcription' && drawerRecord?.image_path && (
                                    <div style={{ marginTop: 12 }}>
                                      <div style={{ fontSize: 11, fontWeight: 600, color: '#722ed1', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Audio Recording</div>
                                      <AudioPlayer audioId={drawerRecord.image_path.trim()} />
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No processing steps recorded for this search." />
              )}
            </div>
          </div>
        )}
      </Drawer>
    );
  };

  const getStatusColor = (statusCode: number): string => {
    if (statusCode >= 500) return 'red';
    if (statusCode >= 400) return 'orange';
    return 'green';
  };

  const getLogLevelColor = (type: string): string => {
    const lowerType = type?.toLowerCase() || '';
    if (lowerType.includes('error') || lowerType.includes('critical')) return 'red';
    if (lowerType.includes('warn')) return 'orange';
    if (lowerType.includes('info')) return 'blue';
    if (lowerType.includes('debug')) return 'default';
    return 'default';
  };

  const handleRefresh = () => {
    if (initialTab === 'event-logs') loadLogs();
    else if (initialTab === 'search-history') loadSearchHistory();
    else if (initialTab === 'system-logs') loadSystemLogs();
  };

  const currentLoading = initialTab === 'event-logs' ? loading : initialTab === 'search-history' ? searchHistoryLoading : systemLogsLoading;

  // Event Logs columns with tooltips
  const eventLogsColumns: TableColumnsType<any> = [
    {
      title: (
        <Tooltip title="When the API request occurred">
          <span>Time</span>
        </Tooltip>
      ),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (value: string) => {
        const { date, time } = formatISTSplit(value);
        return <div style={{ lineHeight: 1.3 }}><div style={{ fontWeight: 500, fontSize: 13 }}>{date}</div><div style={{ fontSize: 11, color: '#8c8c8c' }}>{time}</div></div>;
      },
      sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    },
    {
      title: 'Level',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (value: string) => <Tag color={getLogLevelColor(value)}>{value || 'N/A'}</Tag>,
      filters: [
        { text: 'INFO', value: 'info' },
        { text: 'WARNING', value: 'warn' },
        { text: 'ERROR', value: 'error' },
      ],
      onFilter: (value, record) => record.type?.toLowerCase().includes(value as string),
    },
    { title: 'Endpoint', dataIndex: 'route', key: 'route', ellipsis: true, render: (value: string) => getRouteName(value) },
    { title: 'Method', dataIndex: 'method', key: 'method', width: 100 },
    {
      title: 'Status',
      dataIndex: 'status_code',
      key: 'status_code',
      width: 100,
      render: (value: number) => <Tag color={getStatusColor(value || 200)}>{value || 'N/A'}</Tag>,
      filters: [
        { text: '2xx Success', value: '2' },
        { text: '4xx Client Error', value: '4' },
        { text: '5xx Server Error', value: '5' },
      ],
      onFilter: (value, record) => String(record.status_code || '').startsWith(value as string),
    },
    {
      title: 'Duration',
      dataIndex: 'duration_ms',
      key: 'duration_ms',
      width: 130,
      render: (value: number) => formatDuration(value),
      sorter: (a, b) => (a.duration_ms || 0) - (b.duration_ms || 0),
    },
    { title: 'IP Address', dataIndex: 'ip', key: 'ip', width: 150 },
  ];

  // Search History columns
  const searchHistoryColumns: TableColumnsType<any> = [
    {
      title: 'Time',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (value: string) => {
        const { date, time } = formatISTSplit(value);
        return <div style={{ lineHeight: 1.3 }}><div style={{ fontWeight: 500, fontSize: 13 }}>{date}</div><div style={{ fontSize: 11, color: '#8c8c8c' }}>{time}</div></div>;
      },
      sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    },
    {
      title: 'Query',
      dataIndex: 'query_text',
      key: 'query_text',
      ellipsis: true,
      render: (value: string, record: any) => {
        const isImage = (record.search_type === 'IMAGE' || record.search_type === 'HYBRID') && record.image_path;
        const firstImageId = isImage ? record.image_path.split(',')[0].trim() : null;
        const displayText = value || (isImage ? 'Image Search' : 'N/A');
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {firstImageId && <ImagePreview imageId={firstImageId} size={36} onClick={() => { setPreviewImageIds(record.image_path.split(',').map((id: string) => id.trim())); setPreviewModalOpen(true); }} />}
            <Tooltip title={value || record.image_path || ''}>
              <Text ellipsis>{displayText}</Text>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: 'Results',
      dataIndex: 'results_count',
      key: 'results_count',
      width: 100,
      sorter: (a, b) => (a.results_count || 0) - (b.results_count || 0),
    },
    {
      title: 'Response Time',
      dataIndex: 'response_time_ms',
      key: 'response_time_ms',
      width: 150,
      render: (value: number) => formatDuration(value),
      sorter: (a, b) => (a.response_time_ms || 0) - (b.response_time_ms || 0),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (value: string) => {
        const color = value === 'success' ? 'green' : value === 'error' ? 'red' : value === 'partial' ? 'orange' : 'default';
        return <Tag color={color}>{value || 'N/A'}</Tag>;
      },
      filters: [
        { text: 'Success', value: 'success' },
        { text: 'Error', value: 'error' },
        { text: 'Partial', value: 'partial' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Source',
      dataIndex: 'endpoint_path',
      key: 'source',
      width: 100,
      render: (value: string) => {
        const source = value?.split('/').pop()?.toLowerCase();
        const label = source === 'tvs' ? 'TVS' : source === 'boodmo' ? 'Boodmo' : source === 'smart' ? 'Smart' : null;
        if (!label) return <Text type="secondary">-</Text>;
        const color = source === 'tvs' ? 'blue' : source === 'boodmo' ? 'purple' : 'cyan';
        return <Tag color={color}>{label}</Tag>;
      },
      filters: [
        { text: 'TVS', value: 'tvs' },
        { text: 'Boodmo', value: 'boodmo' },
        { text: 'Smart', value: 'smart' },
      ],
      onFilter: (value, record) => (record.endpoint_path || '').toLowerCase().endsWith(`/${value}`),
    },
    { title: 'IP Address', dataIndex: 'ip_address', key: 'ip_address', width: 150 },
    {
      title: 'Actions',
      key: 'actions',
      width: 70,
      fixed: 'right' as const,
      render: (_: any, record: any) => (
        <Tooltip title="View search details">
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined style={{ fontSize: 16 }} />}
            onClick={() => openTravelDrawer(record)}
            style={{ color: '#1890ff' }}
          />
        </Tooltip>
      ),
    },
  ];

  // System Logs columns
  const systemLogsColumns: TableColumnsType<any> = [
    {
      title: 'Time',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 160,
      render: (value: string) => {
        const { date, time } = formatISTSplit(value);
        return <div style={{ lineHeight: 1.3 }}><div style={{ fontWeight: 500, fontSize: 13 }}>{date}</div><div style={{ fontSize: 11, color: '#8c8c8c' }}>{time}</div></div>;
      },
      sorter: (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
    },
    {
      title: 'Level',
      dataIndex: 'level',
      key: 'level',
      width: 120,
      render: (value: string) => <Tag color={getLogLevelColor(value)}>{value || 'N/A'}</Tag>,
      filters: [
        { text: 'DEBUG', value: 'debug' },
        { text: 'INFO', value: 'info' },
        { text: 'WARNING', value: 'warn' },
        { text: 'ERROR', value: 'error' },
        { text: 'CRITICAL', value: 'critical' },
      ],
      onFilter: (value, record) => record.level?.toLowerCase().includes(value as string),
    },
    { title: 'Service', dataIndex: 'service', key: 'service', width: 150 },
    { title: 'Message', dataIndex: 'message', key: 'message', ellipsis: true },
  ];

  // Render Event Logs content
  const renderEventLogs = () => (
    <>
      {error && (
        <Alert message={error} type="error" closable onClose={() => setError('')} style={{ marginBottom: 16 }} showIcon />
      )}

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Flex wrap="wrap" gap={12} align="center">
          <Tooltip title="Filter logs by event type (e.g., info, error, warn)">
            <Input placeholder="Filter by type..." value={logType} onChange={(e) => setLogType(e.target.value)} style={{ width: 200 }} allowClear />
          </Tooltip>
          <Tooltip title="Filter logs by API route path">
            <Input placeholder="Filter by route..." value={route} onChange={(e) => setRoute(e.target.value)} style={{ width: 250 }} allowClear />
          </Tooltip>
          <Tooltip title="Remove all filters">
            <Button type="text" icon={<ClearOutlined />} onClick={() => { setLogType(''); setRoute(''); }}>Clear Filters</Button>
          </Tooltip>
          <div style={{ flex: 1 }} />
          <Tooltip title="Download event logs as a CSV file for analysis">
            <Button icon={<DownloadOutlined />} onClick={() => exportToCSV(logs, 'event-logs')}>Export CSV</Button>
          </Tooltip>
        </Flex>
      </Card>

      {/* Table */}
      <Card>
        {logs.length > 0 ? (
          <Table
            dataSource={logs.map((item, idx) => ({ key: idx, ...item }))}
            columns={eventLogsColumns}
            expandable={expandableConfig}
            pagination={{
              current: logsPage,
              pageSize: logsPageSize,
              showSizeChanger: true,
              pageSizeOptions: ['10', '25', '50', '100'],
              total: logsTotal,
              showTotal: (t, range) => `Showing ${range[0]}-${range[1]} of ${t}`,
              onChange: (p, ps) => { setLogsPage(p); setLogsPageSize(ps); },
            }}
            scroll={{ x: 'max-content', y: 500 }}
            loading={loading}
            size="small"
            onRow={(record) => getRowStyle(record)}
          />
        ) : !loading ? (
          <EmptyState />
        ) : null}
      </Card>
    </>
  );

  // Render Search History content
  const renderSearchHistory = () => (
    <>
      {searchHistoryError && (
        <Alert message={searchHistoryError} type="error" closable onClose={() => setSearchHistoryError('')} style={{ marginBottom: 16 }} showIcon />
      )}

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Flex wrap="wrap" gap={12} align="center">
          <Tooltip title="Filter by search type: text, hybrid, or image searches">
            <Select
              placeholder="Search Type"
              value={searchTypeFilter || undefined}
              onChange={(value) => setSearchTypeFilter(value || '')}
              style={{ width: 150 }}
              allowClear
              options={[
                { value: 'text', label: 'Text' },
                { value: 'image', label: 'Image' },
                { value: 'hybrid', label: 'Hybrid' },
              ]}
            />
          </Tooltip>
          <Tooltip title="Filter by user session ID to track a specific user's searches">
            <Input placeholder="Filter by session ID..." value={sessionIdFilter} onChange={(e) => setSessionIdFilter(e.target.value)} style={{ width: 250 }} allowClear />
          </Tooltip>
          <Tooltip title="Remove all filters">
            <Button type="text" icon={<ClearOutlined />} onClick={() => { setSearchTypeFilter(''); setSessionIdFilter(''); }}>Clear Filters</Button>
          </Tooltip>
          <div style={{ flex: 1 }} />
          <Tooltip title="Download search history as a CSV file for analysis">
            <Button icon={<DownloadOutlined />} onClick={() => exportToCSV(searchHistory, 'search-history')}>Export CSV</Button>
          </Tooltip>
        </Flex>
      </Card>

      {/* Table */}
      <Card>
        {searchHistory.length > 0 ? (
          <Table
            dataSource={searchHistory.map((item, idx) => ({ key: idx, ...item }))}
            columns={searchHistoryColumns}
            pagination={{
              current: searchPage,
              pageSize: searchPageSize,
              showSizeChanger: true,
              pageSizeOptions: ['10', '25', '50', '100'],
              total: searchHistoryTotal,
              showTotal: (t, range) => `Showing ${range[0]}-${range[1]} of ${t}`,
              onChange: (p, ps) => { setSearchPage(p); setSearchPageSize(ps); },
            }}
            scroll={{ x: 'max-content', y: 500 }}
            loading={searchHistoryLoading}
            size="small"
          />
        ) : !searchHistoryLoading ? (
          <EmptyState description="No search history available for selected period" />
        ) : null}
      </Card>
    </>
  );

  // Render System Logs content
  const renderSystemLogs = () => (
    <>
      {systemLogsError && (
        <Alert message={systemLogsError} type="error" closable onClose={() => setSystemLogsError('')} style={{ marginBottom: 16 }} showIcon />
      )}

      {/* Filters + controls */}
      <Card style={{ marginBottom: 16 }}>
        <Flex wrap="wrap" gap={12} align="center">
          <Tooltip title="Filter by severity: DEBUG (verbose), INFO, WARNING, ERROR, CRITICAL">
            <Select
              placeholder="Log Level"
              value={systemLogLevelFilter || undefined}
              onChange={(value) => setSystemLogLevelFilter(value || '')}
              style={{ width: 150 }}
              allowClear
              options={[
                { value: 'DEBUG', label: 'DEBUG' },
                { value: 'INFO', label: 'INFO' },
                { value: 'WARNING', label: 'WARNING' },
                { value: 'ERROR', label: 'ERROR' },
                { value: 'CRITICAL', label: 'CRITICAL' },
              ]}
            />
          </Tooltip>
          <Tooltip title="Filter by microservice name (e.g., search-service, client-management)">
            <Input placeholder="Filter by service..." value={systemLogServiceFilter} onChange={(e) => setSystemLogServiceFilter(e.target.value)} style={{ width: 200 }} allowClear />
          </Tooltip>
          <Tooltip title="Remove all filters">
            <Button type="text" icon={<ClearOutlined />} onClick={() => { setSystemLogLevelFilter(''); setSystemLogServiceFilter(''); }}>Clear Filters</Button>
          </Tooltip>
          <div style={{ flex: 1 }} />
          <LastUpdated timestamp={lastUpdated} />
          <DateRangePicker startDate={startDate} endDate={endDate} onChange={handleDateRangeChange} />
          <Tooltip title="When enabled, logs refresh automatically every 10 seconds">
            <Space>
              <Switch checked={autoRefresh} onChange={(checked) => setAutoRefresh(checked)} size="small" />
              <Text type="secondary" style={{ fontSize: 13 }}>Auto-refresh</Text>
            </Space>
          </Tooltip>
          <Tooltip title="Manually refresh the current log view">
            <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={currentLoading}>
              {currentLoading ? 'Loading...' : 'Refresh'}
            </Button>
          </Tooltip>
          <Tooltip title="Download system logs as a CSV file for troubleshooting">
            <Button icon={<DownloadOutlined />} onClick={() => exportToCSV(systemLogs, 'system-logs')}>Export CSV</Button>
          </Tooltip>
          <Tooltip title="Permanently delete all system log entries">
            <Button danger icon={<DeleteOutlined />} onClick={handleClearSystemLogs} disabled={systemLogsTotal === 0}>Clear Logs</Button>
          </Tooltip>
        </Flex>
      </Card>

      {/* Table */}
      <Card>
        {systemLogs.length > 0 ? (
          <Table
            dataSource={systemLogs.map((item, idx) => ({ key: idx, ...item }))}
            columns={systemLogsColumns}
            expandable={systemExpandableConfig}
            pagination={{
              current: sysLogsPage,
              pageSize: sysLogsPageSize,
              showSizeChanger: true,
              pageSizeOptions: ['10', '25', '50', '100'],
              total: systemLogsTotal,
              showTotal: (t, range) => `Showing ${range[0]}-${range[1]} of ${t}`,
              onChange: (p, ps) => { setSysLogsPage(p); setSysLogsPageSize(ps); },
            }}
            scroll={{ x: 'max-content', y: 500 }}
            loading={systemLogsLoading}
            size="small"
          />
        ) : !systemLogsLoading ? (
          <EmptyState description="No system logs available for selected period" />
        ) : null}
      </Card>
    </>
  );

  // Render content based on initialTab
  const renderContent = () => {
    switch (initialTab) {
      case 'event-logs':
        return renderEventLogs();
      case 'search-history':
        return renderSearchHistory();
      case 'system-logs':
        return renderSystemLogs();
      default:
        return renderEventLogs();
    }
  };

  return (
    <div>
      {/* Header with tenant selector, date range, auto-refresh and refresh button */}
      {initialTab !== 'system-logs' && (
        <Flex justify="space-between" align="center" wrap="wrap" gap={16} style={{ marginBottom: 16 }}>
          <HierarchyFilter value={hierarchyFilter} onChange={setHierarchyFilter} />
          <Flex align="center" gap={12}>
            <LastUpdated timestamp={lastUpdated} />
            <DateRangePicker startDate={startDate} endDate={endDate} onChange={handleDateRangeChange} />
            <Tooltip title="When enabled, logs refresh automatically every 10 seconds">
              <Space>
                <Switch checked={autoRefresh} onChange={(checked) => setAutoRefresh(checked)} size="small" />
                <Text type="secondary" style={{ fontSize: 13 }}>Auto-refresh</Text>
              </Space>
            </Tooltip>
            <Tooltip title="Manually refresh the current log view">
              <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={currentLoading}>
                {currentLoading ? 'Loading...' : 'Refresh'}
              </Button>
            </Tooltip>
          </Flex>
        </Flex>
      )}

      {/* Content */}
      {renderContent()}

      {/* Travel Log Drawer */}
      {renderTravelDrawer()}

      {/* Image Preview Modal */}
      <Modal
        open={previewModalOpen}
        onCancel={() => setPreviewModalOpen(false)}
        footer={null}
        title="Uploaded Image(s)"
        width={520}
        centered
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center', padding: '12px 0' }}>
          {previewImageIds.map((id) => (
            <ImagePreview key={id} imageId={id} size={200} />
          ))}
        </div>
      </Modal>
    </div>
  );
}

export default Logs;
